import * as queryStringHelpers from "../../lib/queryStringHelpers";
import * as routeHelpers from "../../lib/routeHelpers";
import ActionButton from "../ActionButton";
import callApplicationLead from "../../lib/callApplicationLead";
import Column from "../grid/Column";
import Container from "../grid/Container";
import ErrorBoundary from "../ErrorBoundary";
import Footer from "../Footer";
import Header from "../Header";
import Modal from "../Modal";
import React, {useEffect, useState, useRef} from "react";
import Row from "../grid/Row";
import SetNoIndex from "../../lib/SetNoIndex";
import styles from "./ApprovedPage.module.css";
import TextLink from "../TextLink";
import useLeadsRX from "../../lib/useLeadsRX";
import usePageView from "../../lib/usePageView";

const strings = {
    confirmationLine1: "You are leaving the system and will be redirected to a different page.",
    confirmationLine2: "Are you sure you want to leave?",
    confirmationLabel: "Confirmation",
    continue: "Continue",
    referralProgram: "Refer a Friend",
    review: "Leave us a Review",
    title: "Welcome to 1st Financial Bank USA",
    header: "Congratulations!",
    p1: (isSecuredCard: boolean) =>
        `We are pleased to let you know that your application for a 1st Financial Bank USA ${
            isSecuredCard ? "secured" : ""
        } credit card account has satisfied the approval criteria.${
            isSecuredCard ? "" : " After final review, an account should be opened in your name."
        }`,
    p2:
        "We look forward to welcoming you as a customer and serving you for all the many years to come. You will soon receive a letter from us confirming the action taken on your application. If you have any questions, please call Customer Service at",
    phone: "1-800-733-1732",
};

type Props = {
    isSecuredCard: boolean;
};

export default function ApprovedPage({isSecuredCard}: Props) {
    const LRX_CODE = isSecuredCard ? 14917 : 14395;

    const queryParams = queryStringHelpers.objectify(window.location.search);
    const windowUrl = window.location.href;

    const continueBtnRef = useRef(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    function closeConfirmModal() {
        setIsConfirmModalOpen(false);
    }

    useEffect(() => {
        document.title = strings.title;
    }, []);

    SetNoIndex();
    usePageView(strings.title);
    useLeadsRX(LRX_CODE);

    return (
        <ErrorBoundary>
            <Header />
            <Container className={styles.container}>
                <Row className={styles.titleRow}>
                    <Column>
                        <h1 className={styles.title}>{strings.header}</h1>
                    </Column>
                </Row>
                <Row className={styles.p1Row}>
                    <Column>
                        <div className={styles.paragraph}>{strings.p1(isSecuredCard)}</div>
                    </Column>
                </Row>
                <Row className={styles.p2Row}>
                    <Column>
                        <div className={styles.paragraph}>
                            {strings.p2}{" "}
                            <TextLink
                                openInSameTab={true}
                                className={styles.link}
                                href="tel:800-733-1732"
                                text={strings.phone}
                            />
                            .
                        </div>
                    </Column>
                </Row>
                <Row className={styles.reviewRow}>
                    <Column large={3} medium={2}>
                        <ActionButton
                            label={strings.review}
                            fullWidth={true}
                            onClick={() => {
                                callApplicationLead("review-start", queryParams.dsn, windowUrl);
                                setIsConfirmModalOpen(true);
                            }}
                            ref={continueBtnRef}
                        />
                    </Column>
                </Row>
            </Container>
            <Modal
                id="confirmationModal"
                label={strings.confirmationLabel}
                isOpen={isConfirmModalOpen}
                onClose={closeConfirmModal}
                showCancelButton={true}
                onSubmit={() => {
                    callApplicationLead("review-confirm", queryParams.dsn, windowUrl);
                    closeConfirmModal();
                }}
                onSubmitLabel={strings.continue}
                onSubmitLinkTo={routeHelpers.review(queryParams.dsn)}
                triggerElement={continueBtnRef?.current}
            >
                <div className={styles.modalText}>{strings.confirmationLine1}</div>
                <div className={styles.modalText}>{strings.confirmationLine2}</div>
            </Modal>
            <Footer />
        </ErrorBoundary>
    );
}
